import { BrokenImage } from "@mui/icons-material";
import { Box } from "@mui/material";
import { FarmerImageUpload } from "model";
import { useState } from "react";

export const DiaryEntryImage = ({
  farmerImageUpload,
  style,
  onImageClick,
  onMouseOver,
  onMouseOut,
  border,
}: {
  farmerImageUpload: FarmerImageUpload | undefined;
  style: React.CSSProperties;
  onImageClick: () => void;
  onMouseOver?: () => void;
  onMouseOut?: () => void;
  border?: string;
}) => {
  // Always call hooks first, before any conditional logic
  const [imageError, setImageError] = useState(false);

  // Determine if we should show placeholder
  const shouldShowPlaceholder =
    !farmerImageUpload || !farmerImageUpload.image || imageError;

  // If no image data, show placeholder
  if (shouldShowPlaceholder) {
    // Extract width and height from style to ensure placeholder has correct size
    // For circular images, use the width/height from style
    const width = style.width || "50px";
    const height = style.height || "50px";
    const borderRadius = style.borderRadius || "4px";

    // Calculate icon size based on container size
    let iconSize = "24px";
    if (typeof width === "string" && width.includes("px")) {
      const widthNum = parseInt(width);
      iconSize = `${Math.max(16, Math.min(widthNum * 0.4, 32))}px`;
    }

    return (
      <Box
        sx={{
          width,
          height,
          minWidth: width,
          minHeight: height,
          outline: border,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#e0e0e0",
          border: "2px solid #9e9e9e",
          borderRadius,
          cursor: "pointer",
          flexShrink: 0,
          boxSizing: "border-box",
        }}
        onClick={onImageClick}
        onMouseOver={onMouseOver}
        onMouseOut={onMouseOut}
      >
        <BrokenImage
          sx={{
            color: "#757575",
            fontSize: iconSize,
            width: iconSize,
            height: iconSize,
          }}
        />
      </Box>
    );
  }

  const imageType: string | undefined = farmerImageUpload.imageName
    ?.split(".")
    .pop();

  const imageSrc = `data:image/${imageType || "jpeg"};base64,${
    farmerImageUpload.image
  }`;

  // Validate base64 string length
  if (farmerImageUpload.image.length < 100) {
    console.warn("Diary entry image data seems too short, might be invalid");
  }

  return (
    <img
      alt="Diary entry image"
      src={imageSrc}
      style={{
        ...style,
        outline: border,
        border: style.border || "2px solid white",
        boxShadow: style.boxShadow || "0 2px 4px rgba(0,0,0,0.3)",
        backgroundColor: "#f0f0f0", // Fallback background in case image fails
      }}
      onClick={onImageClick}
      onMouseOver={onMouseOver}
      onMouseOut={onMouseOut}
      onError={(e) => {
        console.error(
          "Failed to load diary entry image:",
          e,
          "Image src length:",
          imageSrc.length
        );
        setImageError(true);
      }}
      onLoad={(e) => {
        const img = e.target as HTMLImageElement;
        console.log("Diary entry image loaded successfully, dimensions:", {
          naturalWidth: img.naturalWidth,
          naturalHeight: img.naturalHeight,
          displayWidth: img.width,
          displayHeight: img.height,
        });
      }}
    />
  );
};
